import { BaseResource } from './base';
import { Enrollment, ListResponse, PaginationParams } from '../types';


export interface CreateEnrollmentData {
    studentId: string;
    courseId: string;
    semester: string;
    enrollmentDate?: string;
}

export interface UpdateEnrollmentData {
    status?: 'enrolled' | 'dropped' | 'completed';
    grade?: string;
}

export interface EnrollmentFilters extends PaginationParams {
    studentId?: string;
    courseId?: string;
    semester?: string;
    status?: 'enrolled' | 'dropped' | 'completed';
    enrollmentDateFrom?: string;
    enrollmentDateTo?: string;
}

export class EnrollmentsResource extends BaseResource {
    /**
     * List all enrollments with optional filtering and pagination
     */
    async list(filters?: EnrollmentFilters): Promise<ListResponse<Enrollment>> {
        return super.requestGet<ListResponse<Enrollment>>('/enrollments', filters);
    }

    /**
     * Get a specific enrollment by ID
     */
    async get(enrollmentId: string): Promise<Enrollment> {
        return super.requestGet<Enrollment>(`/enrollments/${enrollmentId}`);
    }

    /**
     * Create a new enrollment
     */
    async create(data: CreateEnrollmentData): Promise<Enrollment> {
        return super.requestPost<Enrollment>('/enrollments', data);
    }

    /**
     * Update an existing enrollment
     */
    async update(enrollmentId: string, data: UpdateEnrollmentData): Promise<Enrollment> {
        return super.requestPatch<Enrollment>(`/enrollments/${enrollmentId}`, data);
    }

    /**
     * Delete an enrollment
     */
    async delete(enrollmentId: string): Promise<{ success: boolean; message: string }> {
        return super.requestDelete(`/enrollments/${enrollmentId}`);
    }

    /**
     * Bulk enroll students in a course
     */
    async bulkEnroll(courseId: string, studentIds: string[], semester: string): Promise<{ created: Enrollment[]; errors: any[] }> {
        return super.requestPost('/enrollments/bulk', {
            courseId,
            studentIds,
            semester
        });
    }

    /**
     * Drop a student from a course
     */
    async drop(enrollmentId: string, reason?: string): Promise<Enrollment> {
        return super.requestPatch<Enrollment>(`/enrollments/${enrollmentId}/drop`, { reason });
    }

    /**
     * Complete an enrollment (mark as completed)
     */
    async complete(enrollmentId: string, finalGrade?: string): Promise<Enrollment> {
        return super.requestPatch<Enrollment>(`/enrollments/${enrollmentId}/complete`, { finalGrade });
    }
}
